import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';

import {
  Organisation,
  OrganisationAuthorizationsPayload,
  OrganisationCustomizationPayload,
  OrganisationGeneralPayload,
  OrganisationNotificationsPayload,
} from '../models/organisation.model';
import { OrganisationFmsForm } from '../models/organisation-fms.model';
import { Page } from '../models/page.model';

@Injectable({ providedIn: 'root' })
export class OrganisationService {
  private organisationsUrl = '/organisations';
  constructor(private http: HttpClient) {}

  getOrganisations(): Observable<Organisation[]> {
    return this.http.get<any>(this.organisationsUrl + '/all').pipe(
      map((response) => response.data),
      catchError(this.handleError),
    );
  }

  getOrganisation(id: number): Observable<Organisation> {
    return this.http.get<any>(this.organisationsUrl + '/' + id).pipe(
      map((response) => response.data),
      catchError(this.handleError),
    );
  }

  getOrganisationsAdvanced(
    pageIndex: number,
    itemsPerPage: number,
    sortColumnName: string,
    sortColumnOrder: string,
    filter: string,
    excludeUserOrg: boolean = false,
  ): Observable<Page<Organisation>> {
    let url = this.organisationsUrl + '?size=' + itemsPerPage + '&page=' + (pageIndex - 1);
    if (filter) {
      url += '&name=' + encodeURIComponent(filter.trim());
    }
    if (excludeUserOrg) {
      url += '&excludeUserOrganisation=true';
    }
    if (sortColumnName) {
      url += '&sort=' + sortColumnName + ',' + sortColumnOrder;
    }
    return this.http.get<any>(url).pipe(
      map((response) => response.data),
      catchError(this.handleError),
    );
  }

  save(organisation: Organisation): Observable<Organisation> {
    if (organisation.id) {
      return this.put(organisation);
    }
    return this.post(organisation);
  }

  delete(organisation: Organisation) {
    const url = `${this.organisationsUrl}/${organisation.id}`;

    // Rethrow the HttpErrorResponse untouched: the page reads its status to tell
    // "still has users" (400) from other failures.
    return this.http.delete(url);
  }

  // Rethrow the HttpErrorResponse untouched, like delete(): handleSaveError reads its status.
  updateGeneral(id: number, payload: OrganisationGeneralPayload): Observable<Organisation> {
    const url = `${this.organisationsUrl}/${id}/general`;
    return this.http.put<any>(url, payload).pipe(map((res) => res.data));
  }

  updateCustomization(id: number, payload: OrganisationCustomizationPayload): Observable<Organisation> {
    const url = `${this.organisationsUrl}/${id}/customization`;
    return this.http.put<any>(url, payload).pipe(map((res) => res.data));
  }

  // Rethrow the HttpErrorResponse untouched, like delete(): handleSaveError reads its status.
  updateNotifications(id: number, payload: OrganisationNotificationsPayload): Observable<Organisation> {
    const url = `${this.organisationsUrl}/${id}/notifications`;
    return this.http.put<any>(url, payload).pipe(map((res) => res.data));
  }

  // Rethrow the HttpErrorResponse untouched, like delete(): handleSaveError reads its status.
  updateAuthorizations(id: number, payload: OrganisationAuthorizationsPayload): Observable<Organisation> {
    const url = `${this.organisationsUrl}/${id}/authorizations`;
    return this.http.put<any>(url, payload).pipe(map((res) => res.data));
  }

  getFms(id: number): Observable<OrganisationFmsForm> {
    const url = `${this.organisationsUrl}/${id}/fms`;
    return this.http.get<any>(url).pipe(
      map((res) => res.data),
      catchError(this.handleError),
    );
  }

  // Rethrow the HttpErrorResponse untouched, like delete(): handleSaveError reads its status
  // (saveFms reads a 400 to show the "advisor not in organisation" message).
  updateFms(id: number, payload: OrganisationFmsForm): Observable<OrganisationFmsForm> {
    const url = `${this.organisationsUrl}/${id}/fms`;
    return this.http.put<any>(url, payload).pipe(map((res) => res.data));
  }

  private handleError(error: any) {
    console.error('An error occurred', error);
    return throwError(() => error.message || error);
  }

  // Add new organisation
  private post(organisation: Organisation): Observable<Organisation> {
    return this.http.post<any>(this.organisationsUrl, organisation).pipe(
      map((res) => res.data),
      catchError(this.handleError),
    );
  }

  // Update existing organisation
  private put(organisation: Organisation) {
    const url = `${this.organisationsUrl}/${organisation.id}`;

    return this.http.put<any>(url, organisation).pipe(
      map((res) => res.data),
      catchError(this.handleError),
    );
  }
}
